import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { Menu } from 'primeng/menu';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { MenuItem } from 'primeng/api';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';

export interface LanguageOption {
  label: string;
  code: string;
  flag: string;
}

@Component({
  selector: 'app-language-dropdown',
  standalone: true,
  imports: [Menu, Button, Tooltip, FaIconComponent],
  template: `
    <p-button
      [text]="text"
      [rounded]="rounded"
      [size]="size"
      severity="secondary"
      (onClick)="menu.toggle($event)"
      [pTooltip]="tooltip"
      [tooltipPosition]="tooltipPosition">
      <fa-icon [icon]="['fas', 'language']"></fa-icon>
    </p-button>
    <p-menu
      #menu
      [model]="menuItems"
      [popup]="true"
      [appendTo]="'body'"
      [baseZIndex]="10000"
      styleClass="language-dropdown-menu">
    </p-menu>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      :host {
        display: inline-flex;
        align-items: center;
      }

      ::ng-deep .language-dropdown-menu {
        min-width: 180px !important;
        z-index: var(--z-overlay) !important;

        .p-menuitem-link {
          font-size: 0.875rem;
          padding: 0.625rem 0.75rem;
        }

        .p-menuitem-text {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
      }
    `,
  ],
})
export class LanguageDropdownComponent {
  @Input() text = true;
  @Input() rounded = true;
  @Input() size: 'small' | 'large' | undefined = undefined;
  @Input() tooltip = 'Download in another language';
  @Input() tooltipPosition: 'top' | 'bottom' | 'left' | 'right' = 'left';
  @Output() languageSelected = new EventEmitter<string>();

  // Available languages (English excluded since main button downloads English)
  private readonly languages: LanguageOption[] = [
    { label: 'Deutsch', code: 'de', flag: '🇩🇪' },
    { label: 'Français', code: 'fr', flag: '🇫🇷' },
    { label: 'Italiano', code: 'it', flag: '🇮🇹' },
    { label: 'Español', code: 'es', flag: '🇪🇸' },
    { label: 'Português', code: 'pt', flag: '🇵🇹' },
  ];

  menuItems: MenuItem[] = this.languages.map((lang) => ({
    label: `${lang.flag}  ${lang.label}`,
    command: () => this.select(lang.code),
  }));

  private select(languageCode: string): void {
    this.languageSelected.emit(languageCode);
  }
}
